import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Avatar,
  MenuItem,
  Chip,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import "react-quill/dist/quill.snow.css";
import MDEditor from "@uiw/react-md-editor";
import LoadingPage from "../pages/LoadingPage";
import { useTranslation } from "react-i18next";
import countries from "../countries.json";

const ExhibitorForm = ({
  open,
  onClose,
  onSubmit,
  uploading,
  successMessage,
  uploadLoading,
  uploadError,
  errorMessage,
  mode,
  exhibitor: existingExhibitor,
}) => {
  const { t, i18n } = useTranslation();
  const [exhibitor, setExhibitor] = useState(() => {
    if (mode === "edit") {
      return {
        company_name: existingExhibitor?.company_name,
        email: existingExhibitor?.email,
        country: existingExhibitor?.country,
        website: existingExhibitor?.website,
        stallNo: existingExhibitor?.stallNo,
        fascia_board: existingExhibitor?.fascia_board,
        brands: existingExhibitor?.brands || [],
        profile_pic: null,
        company_profile: existingExhibitor?.company_profile,
      };
    } else {
      return {
        company_name: "",
        email: "",
        country: "",
        website: "",
        stallNo: "",
        fascia_board: "",
        brands: [],
        profile_pic: null,
        company_profile: "",
      };
    }
  });
  const [preview, setPreview] = useState(
    mode === "edit" ? existingExhibitor?.profile_pic || "" : ""
  );
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setUploadErrorMessage("Image size should be less than 2MB");
        return;
      }
      setExhibitor({ ...exhibitor, profile_pic: file });
      setPreview(URL.createObjectURL(file));
      setUploadErrorMessage("");
    }
  };

  // const handleSubmit = (e) => {
  //     e.preventDefault();
  //     if (mode === "edit") {
  //         if (!exhibitor.company_name || !exhibitor.email || !exhibitor.country || !exhibitor.website || !exhibitor.stallNo || !exhibitor.fascia_board || !exhibitor.company_profile) {
  //             setUploadErrorMessage('Please fill all required fields');
  //             return;
  //         }

  //         const updateData = {
  //             id: existingExhibitor?.id,
  //             company_name: exhibitor.company_name,
  //             email: exhibitor.email,
  //             country: exhibitor.country,
  //             website: exhibitor.website,
  //             stallNo: exhibitor.stallNo,
  //             fascia_board: exhibitor.fascia_board,
  //             brands: exhibitor.brands,
  //             company_profile: exhibitor.company_profile
  //         };

  //         if (exhibitor.profile_pic instanceof File) {
  //             updateData.profile_pic = exhibitor.profile_pic;
  //         }

  //         onSubmit(updateData);
  //     } else {
  //         if (!exhibitor.company_name || !exhibitor.email || !exhibitor.country || !exhibitor.website || !exhibitor.stallNo || !exhibitor.fascia_board || !exhibitor.profile_pic || !exhibitor.company_profile) {
  //             setUploadErrorMessage('Please fill all fields');
  //             return;
  //         }
  //         setUploadErrorMessage('');
  //         onSubmit({
  //             ...exhibitor
  //         });
  //     }
  //     setExhibitor({
  //         company_name: '',
  //         email: '',
  //         country: '',
  //         website: '',
  //         stallNo: '',
  //         fascia_board: '',
  //         brands: [],
  //         profile_pic: null,
  //         company_profile: ''
  //     });
  //     setPreview('');
  // };

  // Update the form submission handler
  const handleSubmit = (e) => {
    e.preventDefault();
    const requiredFields = [
      "company_name",
      "email",
      "country",
      "website",
      "stallNo",
      "fascia_board",
      "company_profile",
    ];

    const missingFields = requiredFields.filter((field) => !exhibitor[field]);
    if (missingFields.length > 0) {
      setUploadErrorMessage(t("fillAllRequiredFields"));
      return;
    }

    if (mode === "create" && !exhibitor.profile_pic) {
      setUploadErrorMessage(t("uploadLogoRequired"));
      return;
    }

    setUploadErrorMessage("");
    onSubmit({
      ...exhibitor,
      ...(mode === "edit" && { id: existingExhibitor.id }),
    });
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={i18n.dir()}
    >
      <DialogTitle sx={{ bgcolor: "primary.main", color: "white" }}>
        {mode === "edit" ? t("EditExhibitor") : t("AddNewExhibitor")}
      </DialogTitle>
      {uploadLoading ? (
        <LoadingPage />
      ) : (
        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t("CompanyName")}
              value={exhibitor.company_name}
              onChange={(e) =>
                setExhibitor({ ...exhibitor, company_name: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("Email")}
              type="email"
              value={exhibitor.email}
              onChange={(e) =>
                setExhibitor({ ...exhibitor, email: e.target.value })
              }
              margin="normal"
              required
            />
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.label}
              value={
                countries.find(
                  (country) => country.label === exhibitor.country
                ) || null
              }
              onChange={(event, value) => {
                setExhibitor((prev) => ({
                  ...prev,
                  country: value?.label || "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Country")}
                  variant="outlined"
                  margin="normal"
                  required
                />
              )}
            />
            <TextField
              fullWidth
              label={t("Website")}
              type="url"
              value={exhibitor.website}
              onChange={(e) =>
                setExhibitor({ ...exhibitor, website: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("StallNo")}
              value={exhibitor.stallNo}
              onChange={(e) =>
                setExhibitor({ ...exhibitor, stallNo: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("FasciaBoard")}
              value={exhibitor.fascia_board}
              onChange={(e) =>
                setExhibitor({ ...exhibitor, fascia_board: e.target.value })
              }
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>{t("Brands")}</InputLabel>
              <Select
                multiple
                value={exhibitor.brands}
                onChange={(e) =>
                  setExhibitor({ ...exhibitor, brands: e.target.value })
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
              >
                <TextField
                  placeholder={t("TypeAndPressEnter")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value) {
                      setExhibitor((prev) => ({
                        ...prev,
                        brands: [...prev.brands, e.target.value],
                      }));
                      e.target.value = "";
                      e.preventDefault();
                    }
                  }}
                />
              </Select>
            </FormControl>
            <Box sx={{ my: 2 }}>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="logo-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="logo-upload">
                <Button variant="outlined" component="span">
                  {t("UploadLogo")}
                </Button>
              </label>
              <br />
              <span>{t("ForBestViewUseDimension500x300")}</span>
              {preview && (
                <Avatar
                  src={preview}
                  variant="rounded"
                  sx={{
                    width: 250,
                    height: 150,
                    mt: 2,
                    border: "2px dashed",
                    borderColor: "primary.main",
                    objectFit: "contain",
                  }}
                />
              )}
            </Box>
            <Box sx={{ my: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("CompanyProfile")}
              </Typography>
              <MDEditor
                value={exhibitor.company_profile}
                onChange={(value) =>
                  setExhibitor({ ...exhibitor, company_profile: value })
                }
                preview="edit"
                height={200}
                visibleDragbar={false}
              />
            </Box>
            <Box sx={{ my: 2 }}>
              {successMessage && (
                <Typography color="success.main" sx={{ mt: 2 }}>
                  {successMessage}
                </Typography>
              )}
              {(errorMessage || uploadError || uploadErrorMessage) && (
                <Typography color="error.main" sx={{ mt: 2 }}>
                  {errorMessage}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ mx: 1 }} color="secondary">
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={uploading}
          sx={{ mx: 1 }}
        >
          {uploading ? (
            <CircularProgress size={24} color="inherit" />
          ) : mode === "edit" ? (
            t("UpdateExhibitor")
          ) : (
            t("AddExhibitor")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExhibitorForm;
